import * as React from 'react'
import PropTypes from 'prop-types'
import {
  mergeRefs,
  onProps,
  useIntersectionObserver,
  useMediaCondition,
} from '@ds/react-utils'
import { CustomPropTypes } from '../../../support'
import { useThemeStyles } from '../../../theming'
import type { EventListenerProps, DivForwardRef } from '../../../types'
import styles from './styles'
import { isHeightConstrained } from '../../../styles/mediaQueries'

export interface ModalBodyProps extends EventListenerProps<HTMLDivElement> {
  /**
   * The text to present to assistive devices in order to identify the `Modal.Body` content. NOTE: the word "content" will be appended. See `Modal.Base` for use.
   * @internal @ignore
   */
  accessibilityText?: string
  /**
   * The content to display in the `Modal.Body`.
   */
  children: React.ReactNode
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * A React ref to assign to the HTML node representing the `Modal.Body` element.
   */
  forwardedRef?: DivForwardRef
  /**
   * Applies zero padding to the `Modal.Body`.
   */
  noPadding?: boolean
  /**
   * Internal prop to detect if the scrollshadow is needed (when ModalHeader is present).
   * @internal @ignore
   */
  _hasHeader?: boolean
  /**
   * Internal prop to detect if the scrollshadow is needed (when ModalFooter is present).
   * @internal @ignore
   */
  _hasFooter?: boolean
}

export function ModalBody({
  accessibilityText,
  children,
  'data-qa': dataQa,
  forwardedRef,
  noPadding = false,
  _hasHeader,
  _hasFooter,
  ...restProps
}: ModalBodyProps) {
  const [rootElementRef, setRootElementRef] =
    React.useState<HTMLDivElement | null>(null)
  const [targetElementRef, setTargetElementRef] =
    React.useState<HTMLDivElement | null>(null)

  const mergedRefs = React.useMemo(
    () => mergeRefs(forwardedRef, setRootElementRef),
    [forwardedRef],
  )
  const observe = useIntersectionObserver(targetElementRef, rootElementRef)

  const heightConstrained = useMediaCondition(
    isHeightConstrained.replace('@media', ''),
  )

  const sx = useThemeStyles(styles, { noPadding, observe })

  const bodyWrapStyles = [
    sx.default.bodyWrap,
    observe && sx.scrollShadow.bodyWrap,
  ]

  const shadowTopStyles = [
    sx.default.shadowTop,
    observe && sx.scrollShadow.shadowTop,
  ]

  const shadowBottomStyles = [
    sx.default.shadowBottom,
    observe && sx.scrollShadow.shadowBottom,
  ]

  return (
    <>
      {_hasHeader && (
        <span
          aria-hidden="true"
          css={shadowTopStyles}
          data-qa={dataQa && `${dataQa}-scroll-top`}
        />
      )}
      <div
        {...onProps(restProps)}
        aria-label={
          accessibilityText && observe && !heightConstrained
            ? `${accessibilityText} content`
            : undefined
        }
        css={bodyWrapStyles}
        data-qa={dataQa}
        ref={mergedRefs}
        role={observe && !heightConstrained ? 'region' : undefined}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={observe && !heightConstrained ? 0 : undefined}
      >
        <div
          data-qa={dataQa && `${dataQa}-content`}
          ref={setTargetElementRef}
          css={sx.default.body}
        >
          {children}
        </div>
      </div>
      {_hasFooter && (
        <span
          aria-hidden="true"
          css={shadowBottomStyles}
          data-qa={dataQa && `${dataQa}-scroll-bottom`}
        />
      )}
    </>
  )
}

ModalBody.displayName = 'Modal.Body'

ModalBody.propTypes = {
  children: PropTypes.node.isRequired,
  forwardedRef: CustomPropTypes.ReactRef,
  noPadding: PropTypes.bool,
  'on[A-Z].*': PropTypes.func,
}
